import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Loader,
} from "@devhop/ui";
import { SlidersHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import type { UpdateShopSettingsDTO } from "@/modules/merchant/domain/contracts";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { isShopCurrency, SHOP_CURRENCIES } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { usePatchShopSettings } from "../api/queries";

/** ສະກຸນເລີ່ມຕົ້ນ, MC2, VAT — ໃຊ້ທັງແດຊບອດ ແລະ ໜ້າ settings */
export function ShopSettingsFormSection({
  shopId,
  sectionId,
}: {
  shopId: string | undefined;
  sectionId?: string;
}) {
  const { currentShop } = useMerchantShops(shopId);
  const patchSettings = usePatchShopSettings(shopId);

  const [shopDefaultCurrency, setShopDefaultCurrency] = useState<string>("LAK");
  const [defaultTaxPercent, setDefaultTaxPercent] = useState<string>("0");
  const [enforceSingleShopSkuCurrency, setEnforceSingleShopSkuCurrency] =
    useState(false);

  useEffect(() => {
    if (!currentShop) return;
    setShopDefaultCurrency(currentShop.shopDefaultCurrency);
    setDefaultTaxPercent(String(currentShop.defaultTaxPercent ?? 0));
    setEnforceSingleShopSkuCurrency(
      currentShop.enforceSingleShopSkuCurrency ?? false,
    );
  }, [currentShop]);

  const canSubmit = shopId != null && currentShop != null;

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.shop_staff.update}
    >
      <section id={sectionId} className="scroll-mt-24">
        <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
          <CardContent className="space-y-5 sm:space-y-6">
            <div className="flex gap-3 border-border/60 border-b pb-4">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <SlidersHorizontal className="size-5" aria-hidden />
              </span>
              <div>
                <h2 className="font-heading font-semibold text-base text-foreground sm:text-lg">
                  ທົ່ວໄປ
                </h2>
                <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                  ສະກຸນເລີ່ມຕົ້ນສຳລັບ POS ແລະ ຄ່າເລີ່ມຕົ້ນຕອນແກ້ SKU; VAT ໃຊ້ເປີດບິນຊຳລະ.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
              <div className="space-y-2">
                <Label htmlFor="shop-default-currency">ສະກຸນເລີ່ມຕົ້ນຂອງຮ້ານ</Label>
                <SimpleSelect
                  value={shopDefaultCurrency}
                  onValueChange={setShopDefaultCurrency}
                  options={SHOP_CURRENCIES.map((c) => ({ value: c, label: c }))}
                  placeholder="ເລືອກສະກຸນ"
                  disabled={!canSubmit}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shop-default-tax">VAT ເລີ່ມຕົ້ນ POS (%)</Label>
                <Input
                  id="shop-default-tax"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={defaultTaxPercent}
                  onChange={(e) => setDefaultTaxPercent(e.target.value)}
                  disabled={!canSubmit}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/20 p-3 sm:p-4">
              <Checkbox
                id="shop-enforce-sku-currency"
                checked={enforceSingleShopSkuCurrency}
                onCheckedChange={(c) =>
                  setEnforceSingleShopSkuCurrency(c === true)
                }
                disabled={!canSubmit}
                className="mt-0.5"
              />
              <div className="min-w-0">
                <Label
                  htmlFor="shop-enforce-sku-currency"
                  className="cursor-pointer font-medium text-sm"
                >
                  ບັງຄັບສະກຸນ SKU ໃຫ້ກົງກັບສະກຸນຮ້ານ (MC2)
                </Label>
                <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                  ເມື່ອເປີດ: ທຸກ SKU ຕ້ອງໃຊ້ສະກຸນດຽວກັບ &quot;ສະກຸນເລີ່ມຕົ້ນຂອງຮ້ານ&quot; —
                  ປິດກ່ອນແກ້ສະກຸນ SKU ທີ່ຕ່າງກັນ.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:justify-end">
              <Button
                type="button"
                className="w-full sm:w-auto"
                disabled={
                  !canSubmit ||
                  patchSettings.isPending ||
                  !shopId ||
                  !currentShop
                }
                onClick={() => {
                  if (!shopId || !currentShop) return;
                  const tax = Number.parseFloat(defaultTaxPercent);
                  const taxOk = Number.isFinite(tax) && tax >= 0 && tax <= 100;
                  if (!taxOk) {
                    return;
                  }
                  const cur = currentShop.shopDefaultCurrency;
                  const curTax = Number(currentShop.defaultTaxPercent) || 0;
                  const curEnf =
                    currentShop.enforceSingleShopSkuCurrency ?? false;
                  if (!isShopCurrency(shopDefaultCurrency)) return;
                  const body: UpdateShopSettingsDTO = {};
                  if (shopDefaultCurrency !== cur)
                    body.defaultCurrency = shopDefaultCurrency;
                  if (Math.abs(tax - curTax) > 0.0001) {
                    body.defaultTaxPercent = tax;
                  }
                  if (enforceSingleShopSkuCurrency !== curEnf) {
                    body.enforceSingleShopSkuCurrency =
                      enforceSingleShopSkuCurrency;
                  }
                  if (
                    body.defaultCurrency === undefined &&
                    body.defaultTaxPercent === undefined &&
                    body.enforceSingleShopSkuCurrency === undefined
                  ) {
                    return;
                  }
                  // ໃຊ້ .mutate() (ບໍ່ແມ່ນ mutateAsync) — error ຖືກສົ່ງໄປ onError (toast)
                  // ບໍ່ກາຍເປັນ unhandled promise rejection (ບໍ່ເດັ້ງ overlay)
                  patchSettings.mutate(body);
                }}
              >
                {patchSettings.isPending ? <Loader /> : null}
                <span className={patchSettings.isPending ? "ml-2" : ""}>
                  ບັນທຶກ
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </RequireMerchantPermission>
  );
}
